'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import type { ComponentProps } from 'react';
import { Link } from '../../Link/Link';
import { StatusNotification } from '../../StatusNotification/StatusNotification';
import type { RenewalErrorCode } from '../types';
import { getRenewalErrorConfig } from './renewalErrorConfig';

type RenewalErrorNotificationProps = Pick<ComponentProps<'div'>, 'className'> & {
  errorCode: RenewalErrorCode;
};

function RenewalErrorNotification({ errorCode, className }: RenewalErrorNotificationProps) {
  const router = useRouter();
  const t = useTranslations('lotteries.ticket_details.renewal_errors');
  const config = getRenewalErrorConfig(errorCode);

  if (!config) return null;

  const translationKey = config.translationKey satisfies RenewalErrorCode;
  const descriptionLinkHref = config.link?.variant === 'description' ? config.link.href : undefined;
  const chevronHref = config.link?.variant === 'chevron' ? config.link.href : undefined;

  const description = descriptionLinkHref
    ? t.rich(`${translationKey}.description`, {
        link: chunks => (
          <Link
            href={descriptionLinkHref}
            size="explanatory"
            target="_blank"
            data-track-name={config.link?.tracking?.name}
            rel="noopener noreferrer"
            data-testid="renewal-error-description-link"
          >
            {chunks}
          </Link>
        ),
      })
    : t(`${translationKey}.description`);

  return (
    <StatusNotification
      type={config.type}
      label={t(`${translationKey}.label`)}
      description={description}
      showChevron={Boolean(chevronHref)}
      onClick={chevronHref ? () => router.push(chevronHref) : undefined}
      className={className}
      data-testid={`renewal-error-notification-${translationKey}`}
      data-track-type={config.tracking.type}
      data-track-message={config.tracking.message}
    />
  );
}

export { RenewalErrorNotification };
